/**
 * DEV-261 US-9/US-10 — Resumo e envio (Figma 22973 / 23103). "Salvar" é o
 * ponto de não-retorno: loading, um envio só, erro em Sonner persistente que
 * preserva tudo; sucesso leva à tela final.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  PERSISTENT_ERROR_TOAST_ID,
  StepSkeleton,
  SummaryList,
  SummarySection,
  persistentErrorToast,
  toast,
} from '@eureca/ui';
import { validateSubmission } from '../shared/answer-validation';
import { AlignmentApiError, alignmentApi } from '../shared/api';
import { clearDraft } from '../shared/draft';
import { FIELD_MESSAGES } from '../shared/field-copy';
import { AppBar, ProgramHeader } from '../shared/FormHeader';
import { SUMMARY_PENDING } from '../shared/messages';
import { progressPercent } from '../shared/page-rules';
import { clearAlignmentSession } from '../shared/session';
import { buildSummary } from '../shared/summary';
import { GraveErrorScreen, NoConnectionScreen } from '../shared/SystemScreens';
import { descriptionQueryKey, useFormSession } from '../shared/use-form-session';
import { useCatalogue, type StateItem } from '../steps/fields/catalogue';
import { submitErrorTitle } from './submit-error';

export const SUMMARY_TITLE = 'Resumo';

export function SummaryPage({ tenant, formId }: { tenant: string; formId: string }) {
  const form = useFormSession(tenant, formId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inFlight = useRef(false);
  const states = useCatalogue<StateItem>(
    form.session?.sessionToken ?? '',
    'states',
    {},
    !!form.session
  );

  const submitted = form.description?.status === 'submitted';
  useEffect(() => {
    if (submitted) {
      void navigate({
        to: '/descricao-vaga/$tenant/$formId/sucesso',
        params: { tenant, formId },
        replace: true,
      });
    }
  }, [formId, navigate, submitted, tenant]);

  const submit = useMutation({
    mutationFn: () => alignmentApi.submit(form.session?.sessionToken ?? ''),
    onSettled: () => {
      inFlight.current = false;
    },
    onSuccess: () => {
      // Nova tentativa que deu certo encerra o aviso. Não dispensar no início do
      // envio: o `dismiss` do Sonner roda no próximo frame e apagaria o erro de
      // uma falha imediata (o toast de mesmo id já substitui o anterior).
      toast.dismiss(PERSISTENT_ERROR_TOAST_ID);
      clearDraft(form.description?.id ?? '');
      queryClient.removeQueries({ queryKey: descriptionQueryKey(form.description?.id ?? '') });
      void navigate({
        to: '/descricao-vaga/$tenant/$formId/sucesso',
        params: { tenant, formId },
        replace: true,
      });
    },
    onError: (err) => {
      if (err instanceof AlignmentApiError) {
        const leaving =
          err.code === 'already_submitted' ||
          err.code === 'session_expired' ||
          (err.code === 'incomplete' && err.issues.length > 0);
        // Sai do Resumo: o aviso de uma tentativa anterior não vai junto.
        if (leaving) toast.dismiss(PERSISTENT_ERROR_TOAST_ID);
        if (err.code === 'already_submitted') {
          void navigate({
            to: '/descricao-vaga/$tenant/$formId/sucesso',
            params: { tenant, formId },
            replace: true,
          });
          return;
        }
        if (err.code === 'session_expired') {
          clearAlignmentSession(formId);
          void navigate({
            to: '/descricao-vaga/$tenant/$formId',
            params: { tenant, formId },
            replace: true,
          });
          return;
        }
        const first = err.issues[0];
        if (err.code === 'incomplete' && first) {
          // Algo ficou faltando: volta à etapa do primeiro problema, com retorno ao Resumo.
          void navigate({
            to: '/descricao-vaga/$tenant/$formId/etapa/$pageName',
            params: { tenant, formId, pageName: first.page },
            search: { from: 'resumo' },
          });
          return;
        }
      }
      persistentErrorToast(submitErrorTitle(err, navigator.onLine));
    },
  });

  if (form.failure === 'offline') return <NoConnectionScreen onRetry={form.retry} />;
  if (form.failure === 'grave') return <GraveErrorScreen onRetry={form.retry} />;
  if (form.loading || !form.definition || !form.description || submitted) {
    return (
      <div className="min-h-dvh bg-background">
        <AppBar />
        <div className="mx-auto max-w-[640px] py-12">
          <StepSkeleton />
        </div>
      </div>
    );
  }

  const definition = form.definition;
  const stateNames = new Map(states.items.map((s) => [s.acronym, s.name]));
  const sections = buildSummary(definition, form.description.answers, { stateNames });
  const lastPage = definition.pages[definition.pages.length - 1]?.name;
  // QA 08/10/2026 (T056): o espelho das regras aponta o que falta ANTES do envio.
  // O Core continua sendo a autoridade (422 `incomplete` → etapa do problema).
  const pendingPageNames = new Set(
    validateSubmission(definition, form.description.answers).map((i) => i.page)
  );
  const pendingPages = definition.pages.filter((p) => pendingPageNames.has(p.name));
  const hasPending = pendingPages.length > 0;

  function edit(pageName: string) {
    // Sai do Resumo: o aviso de erro de envio fica para trás.
    toast.dismiss(PERSISTENT_ERROR_TOAST_ID);
    void navigate({
      to: '/descricao-vaga/$tenant/$formId/etapa/$pageName',
      params: { tenant, formId, pageName },
      search: { from: 'resumo' },
    });
  }

  return (
    <div className="min-h-dvh bg-background">
      <AppBar />
      <main className="mx-auto flex max-w-[640px] flex-col gap-10 py-12" data-testid="summary-page">
        <ProgramHeader
          programName={definition.program.name}
          logoUrl={definition.program.logoUrl}
          stepTitle={SUMMARY_TITLE}
          progress={progressPercent(definition, 'resumo')}
        />
        <SummaryList>
          {sections.map((section) => (
            <SummarySection
              key={section.key}
              title={section.title}
              alwaysRender={section.alwaysRender}
              editLabel={FIELD_MESSAGES.editar}
              onEdit={() => edit(section.pageName)}
              data-testid={`summary-section-${section.key}`}
              rows={section.rows.map((r) => ({
                label: r.label,
                value: r.value,
                expandable: r.expandable,
                testId: `summary-row-${r.question}`,
              }))}
            />
          ))}
        </SummaryList>
        {hasPending ? (
          <section
            aria-labelledby="summary-pending-title"
            className="flex flex-col gap-3 rounded-lg border border-border bg-muted/40 p-4"
            data-testid="summary-pending"
          >
            <div className="flex flex-col gap-1">
              <h2 id="summary-pending-title" className="text-sm font-medium text-foreground">
                {SUMMARY_PENDING.title}
              </h2>
              <p className="text-sm text-muted-foreground">{SUMMARY_PENDING.body}</p>
            </div>
            <ul className="flex flex-col gap-2">
              {pendingPages.map((page) => (
                <li key={page.name} className="flex items-center justify-between gap-4">
                  <span className="text-sm text-foreground">{page.title}</span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    aria-label={`${SUMMARY_PENDING.action} ${page.title}`}
                    onClick={() => edit(page.name)}
                    data-testid={`summary-pending-edit-${page.name}`}
                  >
                    {SUMMARY_PENDING.action}
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        <div className="flex items-center justify-between">
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="px-8"
            onClick={() => (lastPage ? edit(lastPage) : undefined)}
            data-testid="summary-back"
          >
            {FIELD_MESSAGES.voltar}
          </Button>
          <Button
            type="button"
            size="lg"
            className="px-8"
            loading={submit.isPending}
            disabled={hasPending}
            onClick={() => {
              if (hasPending || inFlight.current || submit.isPending) return;
              inFlight.current = true;
              submit.mutate();
            }}
            data-testid="summary-submit"
          >
            {FIELD_MESSAGES.salvar}
          </Button>
        </div>
      </main>
    </div>
  );
}
